/**
 * Module-level throttles for L2 (WS-reconnect) and L3 (tab-visible)
 * reconcile fetches.
 *
 * Mirrors Apple `JobProgressState.lastReconcileFetchAt` / `lastForeground
 * ReconcileAt` and Android `JobQueueRepository.lastReconcileFetchAtMs` /
 * `lastForegroundReconcileAtMs`. See WEB-L2 and WEB-L3 in
 * docs/REALTIME_RECONCILE_PLAN.md.
 *
 * Two-layer throttle by design: L3 (foreground, 30s outer) gates noisy
 * tab-flap (Cmd-Tab, browser focus loss); L2 (10s inner) dedupes any
 * collision between L3 and the WS-reconnect trigger so the sync bundle
 * never double-fires.
 *
 * Why module-level (not a hook ref): React 19 Strict Mode double-mounts the
 * provider in dev, and the connection-state listener that fires reconnect
 * events lives outside React's render tree (zustand subscription). A module
 * singleton survives both, matching the existing `realtime-sync.locks` and
 * `realtime-sync.registry` patterns in this directory.
 */

export const RECONCILE_FETCH_THROTTLE_MS = 10_000;
export const FOREGROUND_RECONCILE_THROTTLE_MS = 30_000;

let lastReconcileFetchAt = 0;
let lastForegroundReconcileAt = 0;

/**
 * Returns true if the L2 throttle window has elapsed and a reconcile fetch
 * should fire. Caller is responsible for calling `markReconcileFetchFired()`
 * after a successful invocation.
 */
export function shouldRunReconcileFetch(now: number = Date.now()): boolean {
  return now - lastReconcileFetchAt >= RECONCILE_FETCH_THROTTLE_MS;
}

export function markReconcileFetchFired(now: number = Date.now()): void {
  lastReconcileFetchAt = now;
}

export function resetReconcileFetchThrottle(): void {
  lastReconcileFetchAt = 0;
}

/**
 * Returns true if the L3 outer throttle window has elapsed and a foreground
 * reconcile should be considered. Caller still must clear the L2 inner gate
 * before actually running the fetch.
 */
export function shouldRunForegroundReconcile(now: number = Date.now()): boolean {
  return now - lastForegroundReconcileAt >= FOREGROUND_RECONCILE_THROTTLE_MS;
}

export function markForegroundReconcileFired(now: number = Date.now()): void {
  lastForegroundReconcileAt = now;
}

export function resetForegroundReconcileThrottle(): void {
  lastForegroundReconcileAt = 0;
}
